// src/pages/Register.js

import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Link, useNavigate } from 'react-router-dom'
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Grid,
  Paper,
  Avatar,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined'

// Çoklu dil desteği
import { useTranslation } from 'react-i18next'

export default function Register() {
  const { t } = useTranslation() // i18n Hook

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [verificationSent, setVerificationSent] = useState(false)
  const { register } = useAuth()
  const navigate = useNavigate()


  const [checkedUyelikKosullari, setCheckedUyelikKosullari] = useState(false)

  const [openUyelikKosullari, setOpenUyelikKosullari] = useState(false)

  // Dialog açma

  const handleOpenUyelikKosullari = () => setOpenUyelikKosullari(true)

  const handleCloseUyelikKosullari = () => {
    setOpenUyelikKosullari(false)
    setCheckedUyelikKosullari(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
  
    // Checkbox validation
    if ( !checkedUyelikKosullari) {
      return setError(t('registerPage.checkboxError'))
    }
  
    // Password match validation
    if (password !== confirmPassword) {
      return setError(t('registerPage.passwordMismatchError'))
    }
  
    try {
      setError('')
      setLoading(true)
  
      // 1. First validate email with backend
      const formData = new URLSearchParams()
      formData.append('email', email)
  
      const response = await fetch(
        'https://codini-user-singup-gt-795950092325.europe-west3.run.app',
        {
          method: 'POST',
          body: formData,
        }
      )
  
      // Handle HTTP errors
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: t('registerPage.genericError'),
        }))
        throw new Error(errorData.message || t('registerPage.genericError'))
      }
  
      // 2. If backend validation passes, create user account
      await register(email, password)
      setVerificationSent(true)
    } catch (err) {
      // Handle all possible errors (network, auth, backend)
      setError(err.message || t('registerPage.genericError'))
    } finally {
      setLoading(false)
    }
  }

  if (verificationSent) {
    return (
      <Container component="main" maxWidth="xs">
        <Paper elevation={6} sx={{ p: 3, mt: 6, borderRadius: 2 }}>
          <Typography variant="h5" gutterBottom>
            {t('registerPage.successTitle')}
          </Typography>
          <Typography gutterBottom>
            {t('registerPage.successDesc')}
          </Typography>
          <Box mt={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/login')}
            >
              {t('registerPage.goToLoginPageButton')}
            </Button>
          </Box>
        </Paper>
      </Container>
    )
  }

  return (
    <Container component="main" maxWidth="xs">
      <Paper elevation={6} sx={{ p: 3, mt: 6, borderRadius: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Avatar sx={{ mb: 1, bgcolor: 'secondary.main' }}>
            <PersonAddOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            {t('registerPage.title')}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  required
                  fullWidth
                  type="email"
                  label={t('registerPage.emailLabel')}
                  variant="outlined"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  required
                  fullWidth
                  type="password"
                  label={t('registerPage.passwordLabel')}
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  required
                  fullWidth
                  type="password"
                  label={t('registerPage.confirmPasswordLabel')}
                  variant="outlined"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </Grid>


              {/* 3. CheckBox - Üyelik Koşulları */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={checkedUyelikKosullari}
                      onChange={() => {
                        if (!checkedUyelikKosullari) handleOpenUyelikKosullari()
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {t('registerPage.membershipTermsChecked')}
                    </Typography>
                  }
                  sx={{ alignItems: 'flex-start' }}
                />
              </Grid>
            </Grid>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ mt: 3, mb: 1, py: 1 }}
            >
              {loading 
                ? <CircularProgress size={24} color="inherit" /> 
                : t('registerPage.registerButton')
              }
            </Button>

            <Grid container justifyContent="flex-end">
              <Grid item>
                <Link to="/login" style={{ textDecoration: 'none' }}>
                  <Typography variant="body2" color="primary">
                    {t('registerPage.alreadyHaveAccount')}
                  </Typography>
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Paper>

     
      {/* 3. Popup (Dialog) - Üyelik Koşulları */}
      <Dialog
        open={openUyelikKosullari}
        onClose={() => setOpenUyelikKosullari(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('registerPage.membershipTitle')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" paragraph>
            {t('registerPage.membershipBody')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleCloseUyelikKosullari}>
            {t('registerPage.membershipRead')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}